/**
 * BusinessPageApp: a fake, sandboxed clone of the live public business
 * profile at `/business/:companyId` (see PublicBusiness.jsx).  Same visual
 * shape (hero, fleet grid with cover photos, availability checker, price
 * estimate, guest form, business terms, DEMO banner), zero network writes.
 *
 * The promoter uses this to show an owner:
 *   "This is the customer-facing page every ConfirmPH business gets by
 *    default, and when a customer taps 'Send booking request' here, it
 *    lands inside your Booking Intake without a single manual step."
 */

import { useMemo, useState } from "react";
import {
  Car, Calendar, ShieldCheck, CheckCircle2, ArrowRight, AlertTriangle,
} from "lucide-react";
import { DemoBadge } from "../DemoPanels";

const fmt = (n) => "₱" + Number(n || 0).toLocaleString("en-PH");

export default function BusinessPageApp({ business, onSubmitInquiry }) {
  const vehicles = useMemo(() => business.vehicles || [], [business.vehicles]);
  const [selectedId, setSelectedId] = useState(vehicles[0]?.id);
  const [start, setStart] = useState(business.suggested?.start || "");
  const [end, setEnd] = useState(business.suggested?.end || "");
  const [checked, setChecked] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [accepted, setAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const selected = useMemo(
    () => vehicles.find((v) => v.id === selectedId),
    [vehicles, selectedId]
  );
  const days = useMemo(() => {
    if (!start || !end) return 0;
    return Math.max(1, Math.ceil((new Date(end) - new Date(start)) / 86400000));
  }, [start, end]);
  const total = (selected?.rate || 0) * days;
  const canSubmit = accepted && form.name && form.email && checked;

  const submit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    onSubmitInquiry?.({
      customer: form.name, email: form.email, phone: form.phone,
      vehicle: selected?.name, days, total,
      start, end, deposit: business.deposit_required,
    });
  };

  return (
    <div data-testid="business-page-app"
         className="min-h-full bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Sticky demo banner: mirrors the real DemoBusinessGuard */}
      <div className="sticky top-0 z-10 bg-rose-600 text-white px-3 py-1.5 text-xs font-semibold flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> Demo business, not a real rental
        </span>
        <DemoBadge>PREVIEW</DemoBadge>
      </div>

      {/* Business hero */}
      <div className="px-4 sm:px-6 py-5 border-b border-slate-100">
        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-semibold flex items-center gap-1">
          <ShieldCheck className="w-3.5 h-3.5" /> Listed on ConfirmPH
        </div>
        <h1 className="mt-1 font-display text-2xl sm:text-3xl font-semibold text-slate-900">
          {business.name}
        </h1>
        <p className="text-sm text-slate-600 mt-1">{business.tagline}</p>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
          <span>⭐ {business.rating} · {business.review_count} reviews</span>
          <span>·</span>
          <span>{business.location}</span>
        </div>
      </div>

      {submitted ? (
        <SubmittedCard business={business} form={form}
                        selected={selected} days={days} total={total} />
      ) : (
        <div className="px-4 sm:px-6 py-5 space-y-5">
          {/* Fleet */}
          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">
              Choose a vehicle
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {vehicles.map((v) => (
                <VehicleCard key={v.id} v={v}
                              selected={selectedId === v.id}
                              onSelect={() => { setSelectedId(v.id); setChecked(false); }} />
              ))}
            </div>
          </section>

          {/* Date picker */}
          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">
              When do you need it?
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Pickup</span>
                <input type="datetime-local" value={start}
                        onChange={(e) => { setStart(e.target.value); setChecked(false); }}
                        data-testid="bp-start"
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Return</span>
                <input type="datetime-local" value={end}
                        onChange={(e) => { setEnd(e.target.value); setChecked(false); }}
                        data-testid="bp-end"
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </label>
            </div>
            <button onClick={() => setChecked(true)}
                    disabled={!start || !end || !selected}
                    data-testid="bp-check-availability"
                    className="mt-3 px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center gap-1 disabled:opacity-60">
              <Calendar className="w-3.5 h-3.5" /> Check availability & price
            </button>
            {checked && selected && (
              <div data-testid="bp-availability-result"
                   className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                Available · <strong>{fmt(total)}</strong> ({days} day{days === 1 ? "" : "s"})
                {business.deposit_required > 0 && <> · Security deposit: <strong>{fmt(business.deposit_required)}</strong></>}
              </div>
            )}
          </section>

          {/* Guest form: appears after availability check succeeds */}
          {checked && (
            <form onSubmit={submit} className="grid gap-3" data-testid="bp-guest-form">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                Your details
              </div>
              <input type="text" placeholder="Your name" value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      data-testid="bp-name"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              <input type="email" placeholder="Email" value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      data-testid="bp-email"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              <input type="tel" placeholder="Phone (optional)" value={form.phone}
                      onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                      data-testid="bp-phone"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                <strong>Business terms:</strong>
                <p className="mt-0.5">{business.terms}</p>
                <p className="text-[10px] text-slate-500 mt-2">
                  ConfirmPH is the platform that helps confirm and track this booking.
                  The rental contract is between you and the business above.
                </p>
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={accepted}
                        onChange={(e) => setAccepted(e.target.checked)}
                        data-testid="bp-accept" />
                <span>I accept the business terms and the ConfirmPH disclaimer.</span>
              </label>
              <button type="submit" disabled={!canSubmit}
                      data-testid="bp-submit"
                      className="px-5 py-3 rounded-lg btn-brand font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2">
                Send booking request <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

function VehicleCard({ v, selected, onSelect }) {
  return (
    <button type="button" onClick={onSelect}
            data-testid={`bp-vehicle-${v.id}`}
            className={`text-left rounded-xl border overflow-hidden transition ${
              selected ? "border-emerald-500 ring-2 ring-emerald-500/40 bg-emerald-50"
                       : "border-slate-200 bg-white hover:border-slate-300"
            }`}>
      <div className="relative aspect-[4/3] bg-slate-100">
        <img src={v.photo} alt={v.name} className="w-full h-full object-cover"
             onError={(e) => { e.currentTarget.style.display = "none"; }} />
        {selected && (
          <span className="absolute top-2 right-2 inline-flex items-center gap-1 bg-emerald-600 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
            <CheckCircle2 className="w-3 h-3" /> Selected
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-start gap-2">
          <Car className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{v.name}</div>
            <div className="text-xs text-slate-500">{v.transmission} · {v.seats} seats</div>
          </div>
        </div>
        <div className="mt-2 text-sm">
          <strong>{fmt(v.rate)}</strong> <span className="text-slate-500 text-xs">/ day</span>
        </div>
      </div>
    </button>
  );
}

function SubmittedCard({ business, form, selected, days, total }) {
  return (
    <div className="px-4 sm:px-6 py-6" data-testid="bp-submitted">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
        <div className="inline-flex items-center gap-1 text-emerald-800 font-semibold">
          <CheckCircle2 className="w-5 h-5" /> Booking request sent
        </div>
        <p className="mt-2 text-sm text-slate-700">
          Thanks {form.name}! {business.name} will confirm your {selected?.name} rental for {days} day{days === 1 ? "" : "s"} ({"₱" + Number(total).toLocaleString("en-PH")}) shortly. You&apos;ll get a confirmation link by email.
        </p>
        <p className="mt-3 text-xs text-slate-500">
          The rental owner just received this inside their ConfirmPH Booking Intake: switch to the Business shell using the toggle above to see it arrive.
        </p>
      </div>
    </div>
  );
}
